import { Injectable } from '@angular/core';
import {HeaderService} from "./app/services/header/header.service";
import {QChatControllerService} from "./app/services/q-chat-controller.service";
import {IinOrBinCheckResult, OcpUtils} from "./OcpUtils";
import {TranslateService} from "@ngx-translate/core";
import {Subject} from "rxjs";
import {MainService} from "./app/services/main/main.service";
import {HttpClient} from "@angular/common/http";

interface QChatListener {
  subject: string;
  payload: {
    type: string;
    data?: any;
  };
}

declare global {
  interface Window {
    RocketChat?: (callback: () => void) => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class RocketChatService {
  private isMounted = false;
  private qChatElement: HTMLElement | null = null;
  userData: any = {
    userInfo:{
      isModerator: false
    },
    companyList: []
  };

  private readonly QCHAT_ELEMENT = "wc-video-call";
  private readonly QCHAT_HOST = "https://qbox.telecom.kz";
  private readonly QCHAT_DOMAIN = "qbox.telecom.kz";

  // Subject для открытия QChat из Universal callback button
  public openQChat$ = new Subject<void>();
  // Subject для закрытия QChat
  public closeQChat$ = new Subject<void>();
  public newMessageQChat$ = new Subject<void>();

  constructor(
    private headerService: HeaderService,
    private qChatControllerService: QChatControllerService,
    private translate: TranslateService,
    private main: MainService,
    private http: HttpClient,
  ) {
  }

  async openWidget(): Promise<void> {
    // Эмитим событие для открытия Universal callback button с QChat
    this.openQChat$.next();
  }

  async mountQChat(container: HTMLElement): Promise<void> {
    if (this.isMounted) return;

    let isDefined = customElements.get(this.QCHAT_ELEMENT);
    if (!isDefined) {
      await customElements.whenDefined(this.QCHAT_ELEMENT).catch(() => {});
    }

    this.isMounted = true;

    const videoCallElem = document.createElement(this.QCHAT_ELEMENT);
    videoCallElem.setAttribute("id", "wc-video-call");
    videoCallElem.setAttribute("host-name", this.QCHAT_HOST);
    videoCallElem.setAttribute("domain", this.QCHAT_DOMAIN);

    const uin = this.headerService.currentCompany;
    const data: any = {};
    if (uin) {
      const checkResult = OcpUtils.isIinOrBin(uin);
      data[checkResult === IinOrBinCheckResult.IIN ? 'iin' : 'bin'] = uin;
    }

    videoCallElem.setAttribute("extra", JSON.stringify(data));
    videoCallElem.setAttribute("call-media", "text");
    videoCallElem.setAttribute("height", this.detectMob() ? "100dvh" : "50dvh");
    videoCallElem.setAttribute("show-hangup-call-button", "false");
    videoCallElem.setAttribute("topic", "test_b2b");
    videoCallElem.setAttribute("lang", this.translate.currentLang || 'ru');
    videoCallElem.setAttribute("head-title", "Личный кабинет");
    videoCallElem.setAttribute("customer-type", "organization");
    videoCallElem.setAttribute("start-chat-with-first-message", "true");

    this.initChatData().then(userId => {
      if (userId) {
        videoCallElem.setAttribute("user-id", userId);
      }
    }).catch(() => {});

    videoCallElem.addEventListener("wc", (event: any) => {
      if (event.detail?.length) {
        const detail = event.detail as QChatListener[];
        detail.forEach((item) => {
          if (item.subject === 'call') {
            if (item.payload.type === 'state' && item.payload.data?.state === 'ended') {
              this.unmountQChat();
            } else if (item.payload.type === 'chat' && item.payload.data?.text === 'Оператор завершил диалог') {
              this.unmountQChat();
            } else if (item.payload.type === 'chat') {
              this.newMessageQChat$.next();
            }
          }
        });
      }
    });

    container.appendChild(videoCallElem);
    this.qChatElement = videoCallElem;
  }

  unmountQChat(): void {
    if (this.qChatElement) {
      this.qChatElement.remove();
      this.qChatElement = null;
      this.isMounted = false;
      // Уведомляем компонент о закрытии QChat
      this.closeQChat$.next();
    }
  }

  detectMob() {
    const toMatch = [
      /Android/i, /webOS/i, /iPhone/i, /iPad/i,
      /iPod/i, /BlackBerry/i, /Windows Phone/i,
      /ismet-mobile/i
    ];
    return toMatch.some((toMatchItem) => {
      return navigator.userAgent.match(toMatchItem);
    });
  }

  async initChatData(): Promise<string | null> {
    try {
      const data = await this.getUserInfo();
      const bin = this.headerService.currentCompany;
      const accountResponse = await this.customerAccountByUin(bin);
      const account = accountResponse?.data?.AdvancedCustomerAccountByUin?.account;

      if (account) {
        return new Promise<string | null>((resolve) => {
          this.qChatControllerService.getQChatUserId({
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            iin: bin,
            patronymic: data.patronymic,
            phone: data.phone,
            customer_id: account.customerId,
            account_id: account.id
          }).subscribe({
            next: (userId) => resolve(userId),
            error: () => resolve(null)
          });
        });
      }

      return null;
    } catch {
      return null;
    }
  }

  getUserInfo(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.main.userInfo().subscribe({
        next: (res: any) => {
          resolve(res);
        },
        complete: () => {
        },
        error: (err) => {
          reject(err);
        }
      });
    });
  }

  customerAccountByUin(bin: string, woCache: boolean = false): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post<any>(`/proxy/participant-api.bpmn/graphql`,
        {
          "operationName":"AdvancedCustomerAccountByUin",
          "query": `query AdvancedCustomerAccountByUin {
        AdvancedCustomerAccountByUin(uin: "${bin}", withoutCache: ${woCache}) {
          subscription {
            subscriptionCount {
              totalCount
            }
          }
          agreement {
            createDate
            closeDate
            id
            agreementNum
          }
          balance {
            balance {
              data {
                outMoney
                credit
                debit
                inMoney
              }
            }
            balanceOFD {
              balance
              fine
              toPay
            }
          }
          account {
            docflowAgreementId
            docflowAgreementUri
            filialNameRu
            filialNameKz
            filialId
            active
            customerId
            externalId
            agreementId
            localId
            customerAccountCategoryId
            customerAccountTypeId
            id
            filialCode
          }
        }
      }`
        }).subscribe({
        next: (res) => resolve(res),
        error: (err) => reject(err)
      });
    });
  }
}
